import React, { useEffect, useState } from "react";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import { useLocale } from "@insightfulscience/smart-react/i18";
import Line from "@insightfulscience/atomic-react/Line";
import LandingSimpleSectionStripped from "@insightfulscience/atomic-react/LandingElement/SimpleSectionStripped";
import LandingSimpleSection from "@insightfulscience/atomic-react/LandingElement/SimpleSection";
import Feature from "@insightfulscience/atomic-react/Feature";
import FeatureImage from "@insightfulscience/atomic-react/Feature/Image";
import FeatureMeta from "@insightfulscience/atomic-react/Feature/Meta";
import UList from "@insightfulscience/atomic-react/UList";
import Html from "@insightfulscience/smart-react/Html";
import SimpleTemplate from "@insightfulscience/atomic-react/SimpleTemplate";
import SimpleTemplateTitle from "@insightfulscience/atomic-react/SimpleTemplate/Title";
import SimpleTemplateDescription from "@insightfulscience/atomic-react/SimpleTemplate/Description";
import { Col, Row } from "@insightfulscience/atomic-react/Grid";
import Block from "@insightfulscience/atomic-react/Block";
import Paragraph from "@insightfulscience/atomic-react/Paragraph";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import SiteLayout from "../../SiteLayout";
import SectionTryForFree from "../../SectionTryForFree";
import withErrorHandling from "../../AppError/withErrorHandling";
import { publicConfig } from "../../config";
import { HubSpotForm } from "../../HubSpotForm";

const FreeCourseLicense = () => {
	const [t] = useLocale("free-course-license");

	const [viewSubtitle, setViewSubtitle] = useState(true);

	const hideFormSubtitle = () => setViewSubtitle(false);

	useEffect(() => {
		document.addEventListener("formSubmitted", hideFormSubtitle);
		return () => document.removeEventListener("formSubmitted", hideFormSubtitle);
	}, []);

	const onFormSubmitted = () => {
		const event = new Event("formSubmitted");
		document.dispatchEvent(event);
	};

	return (
		<SiteLayout>
			<PageMeta title={t("META.TITLE")} description={t("META.DESCRIPTION")}>
				<PageMeta.OGP title={t("META.TITLE")} description={t("META.DESCRIPTION")} />
				<PageMeta.Canonical href={t("META.CANONICAL")} />
			</PageMeta>
			<Hero spacing={{ py: 20 }} utility={{ bgColor: "transparent" }}>
				<HeroTitle>{t("HERO_TITLE")}</HeroTitle>
				<HeroAction>
					<HeroRow>
						<HeroCol>
							<HeroButtonLink isPreLine href={t("HERO_ACTION_HREF")}>
								{t("HERO_ACTION")}
							</HeroButtonLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
			</Hero>
			<Line />
			<LandingSimpleSectionStripped>
				<LandingSimpleSection.TitleV2>{t("SECTION_TITLE")}</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.DescriptionV2>
					{t("SECTION_DESCRIPTION")}
				</LandingSimpleSection.DescriptionV2>
				<Feature>
					<FeatureImage md={6} src="/images/geneious/course-licenses/image-1.png" />
					<FeatureMeta md={6}>
						<FeatureMeta.Description>
							<UList type="pros">
								<UList.Item>{t("LIST_ITEM_1")}</UList.Item>
								<UList.Item>{t("LIST_ITEM_2")}</UList.Item>
								<UList.Item>{t("LIST_ITEM_3")}</UList.Item>
								<UList.Item>{t("LIST_ITEM_4")}</UList.Item>
								<UList.Item>{t("LIST_ITEM_5")}</UList.Item>
								<UList.Item>
									<Html Tag="span">{t("LIST_ITEM_6")}</Html>
								</UList.Item>
							</UList>
						</FeatureMeta.Description>
					</FeatureMeta>
				</Feature>
			</LandingSimpleSectionStripped>
			<LandingSimpleSectionStripped spacing={{ py: 0 }}>
				<SimpleTemplate
					utility={{ bgColor: "transparent" }}
					panelUtility={{ radius: 16 }}
					shadow={false}
					paddingTop="pt-0"
					paddingBottom="pb-0"
				>
					<SimpleTemplateTitle>{t("FORM_TITlE")}</SimpleTemplateTitle>
					{viewSubtitle && (
						<SimpleTemplateDescription>{t("FORM_DESCRIPTION")}</SimpleTemplateDescription>
					)}
					<HubSpotForm
						formId={`${publicConfig.HUBSPOT_FREE_COURSE_LICENSE_FORM_ID}`}
						portalId={`${publicConfig.HUBSPOT_PORTAL_ID}`}
						target="freeCourseLicenseForm"
						formSubmitFunc={onFormSubmitted}
					/>
				</SimpleTemplate>
			</LandingSimpleSectionStripped>
			<LandingSimpleSection isBorderNone>
				<LandingSimpleSection.TitleV2 spacing={{ mb: 15 }}>{t("FAQ")}</LandingSimpleSection.TitleV2>
				<LandingSimpleSection.Body>
					<Row mx>
						<Col md={6}>
							<Block spacing={{ mb: 4 }}>
								<Paragraph sx={{ fontSize: "xl", fontWeight: "medium" }}>
									{t("FAQ_ITEM_1_TITLE")}
								</Paragraph>
								<Paragraph spacing={{ mt: 5 }}>{t("FAQ_ITEM_1_DESCRIPTION")}</Paragraph>
							</Block>
						</Col>
						<Col md={6}>
							<Block spacing={{ mb: 4 }}>
								<Paragraph sx={{ fontSize: "xl", fontWeight: "medium" }}>
									{t("FAQ_ITEM_2_TITLE")}
								</Paragraph>
								<Paragraph spacing={{ mt: 5 }}>{t("FAQ_ITEM_2_DESCRIPTION")}</Paragraph>
							</Block>
						</Col>
					</Row>
					<Row mx spacing={{ mt: 5 }}>
						<Col md={6}>
							<Block spacing={{ mb: 4 }}>
								<Paragraph sx={{ fontSize: "xl", fontWeight: "medium" }}>
									{t("FAQ_ITEM_3_TITLE")}
								</Paragraph>
								<Paragraph spacing={{ mt: 5 }}>{t("FAQ_ITEM_3_DESCRIPTION")}</Paragraph>
							</Block>
						</Col>
						<Col md={6}>
							<Block spacing={{ mb: 4 }}>
								<Paragraph sx={{ fontSize: "xl", fontWeight: "medium" }}>
									{t("FAQ_ITEM_4_TITLE")}
								</Paragraph>
								<Paragraph spacing={{ mt: 5 }}>{t("FAQ_ITEM_4_DESCRIPTION")}</Paragraph>
							</Block>
						</Col>
					</Row>
				</LandingSimpleSection.Body>
			</LandingSimpleSection>
			<SectionTryForFree />
		</SiteLayout>
	);
};

export default pipe(FreeCourseLicense, withErrorHandling);
